<script lang="ts" setup>
import type { DateRange, DateValue } from 'reka-ui';
import type { Temporal } from 'temporal-polyfill';
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatPlainDate, useUserLocale } from '#layers/base/public';
import { calendarRangeUi } from '#layers/ui/utils/calendar-ui';
import DateTimePickerPanelShell from './PanelShell.vue';

interface Props extends Pick<ButtonProps, 'size' | 'disabled'> {
	readonly?: boolean;
	panel: 'start' | 'end';
	selectedDate?: Temporal.PlainDate | null;
	displayRange: DateRange;
	allowNonContiguousRanges: boolean;
	isDateUnavailable: (date: DateValue) => boolean;
	isSetToNowDisabled: boolean;
	/** When false, the clear affordance is hidden (e.g. a required range bound). */
	showClear?: boolean;
}

const { readonly = false, showClear = true, panel, selectedDate } = defineProps<Props>();
const emit = defineEmits<{
	'clear': [];
	'setToNow': [];
	'update:calendar': [value: DateRange | null];
}>();

defineSlots<{ extra?: () => unknown }>();

const placeholder = defineModel<DateValue>('placeholder', { required: true });
const { t } = useI18n();
const { formatLocale } = useUserLocale();

const label = computed(() =>
	panel === 'start' ? t('date_range_picker.start_date') : t('date_range_picker.end_date'),
);

const fixedDate = computed(() => (panel === 'start' ? 'end' : 'start'));

const panelTestId = computed(() => `date-range-${panel}-panel`);
const clearTestId = computed(() => `date-range-clear-${panel}`);
const selectedDateDisplay = computed(() => formatPlainDate(selectedDate ?? null, formatLocale.value));
</script>

<template>
	<DateTimePickerPanelShell
		:data-testid="panelTestId"
		:label
		:display-value="selectedDateDisplay"
		:placeholder="$t('date_range_picker.select_a_date')"
		:size
		:disabled
		:readonly
		:can-clear="showClear && selectedDate != null"
		:is-set-to-now-disabled
		:clear-data-testid="clearTestId"
		set-to-now-data-testid="date-range-set-to-now"
		@clear="emit('clear')"
		@set-to-now="emit('setToNow')"
	>
		<DCalendar
			v-model:placeholder="placeholder"
			:model-value="displayRange"
			range
			:fixed-date
			:year-controls="false"
			:allow-non-contiguous-ranges
			:is-date-unavailable
			:ui="calendarRangeUi"
			@update:model-value="emit('update:calendar', $event)"
		/>
		<slot name="extra" />
	</DateTimePickerPanelShell>
</template>
